import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { customerService } from '../services/customerService';

const useCustomerManagement = () => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [showCustomerHistoryModal, setShowCustomerHistoryModal] = useState(false);

  // Auto-search when query changes with AbortController for cleanup
  useEffect(() => {
    if (customerSearchQuery.length < 2) {
      setCustomerSuggestions([]);
      return;
    }

    const controller = new AbortController();
    let isMounted = true;

    const searchCustomers = async () => {
      setCustomerSearchLoading(true);
      try {
        // Trzeci argument searchCustomers to ROLA, nie AbortSignal. Przekazanie
        // signala serializowalo sie do '{}', filtr roli nie dochodzil do backendu,
        // a zapytanie ma ORDER BY jest_dostawca DESC - dostawcy (np. SFD) ladowali
        // na gorze podpowiedzi klienta na kasie.
        const response = await customerService.searchCustomers(customerSearchQuery, 10, 'odbiorca');
        if (isMounted) {
          if (response.success) {
            setCustomerSuggestions(response.data.customers || []);
            setShowCustomerSuggestions(true);
          } else {
            setCustomerSuggestions([]);
          }
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        console.error("Blad wyszukiwania klientow:", err);
        if (isMounted) setCustomerSuggestions([]);
      } finally {
        if (isMounted) setCustomerSearchLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      searchCustomers();
    }, 300);

    return () => {
      isMounted = false;
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [customerSearchQuery]);

  const loadAllCustomers = async () => {
    setCustomerSearchLoading(true);
    try {
      const response = await customerService.getCustomers();
      const customers = Array.isArray(response) ? response : response.data || [];
      setCustomerSuggestions(customers);
      setShowCustomerSuggestions(true);
    } catch (err) {
      console.error("Blad pobierania klientow:", err);
      setCustomerSuggestions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    const displayName =
      (customer.nazwa_firmy && customer.nazwa_firmy.trim()) ||
      (customer.name && customer.name.trim()) ||
      `${(customer.imie || "").trim()} ${(customer.nazwisko || "").trim()}`.trim() ||
      customer.email ||
      customer.phone ||
      `Klient #${customer.id}`;
    setCustomerSearchQuery(displayName);
    setShowCustomerSuggestions(false);
  };

  const handleNewCustomerAdded = (newCustomer) => {
    selectCustomer(newCustomer);
    setShowAddCustomerModal(false);
  };

  const handleEditSelectedCustomer = async () => {
    if (!selectedCustomer) return;
    try {
      const fullCustomerData = await customerService.getCustomer(selectedCustomer.id);
      const customerToEdit = {
        id: fullCustomerData.id,
        typ_klienta: fullCustomerData.typ_klienta || (fullCustomerData.nip ? 'firma' : 'osoba_fizyczna'),
        imie: fullCustomerData.imie || '',
        nazwisko: fullCustomerData.nazwisko || '',
        nazwa_firmy: fullCustomerData.nazwa_firmy || '',
        email: fullCustomerData.email || '',
        telefon: fullCustomerData.telefon || '',
        telefon_kod: '+48',
        nip: fullCustomerData.nip || '',
        regon: fullCustomerData.regon || '',
        adres: fullCustomerData.adres || fullCustomerData.ulica || '',
        miasto: fullCustomerData.miasto || '',
        kod_pocztowy: fullCustomerData.kod_pocztowy || '',
        kraj: fullCustomerData.kraj || 'Polska',
        kraj_kod: fullCustomerData.kraj_kod || 'PL',
        wysylka_inna: fullCustomerData.wysylka_inna || false,
        wysylka_imie: fullCustomerData.wysylka_imie || '',
        wysylka_nazwisko: fullCustomerData.wysylka_nazwisko || '',
        wysylka_firma: fullCustomerData.wysylka_firma || '',
        wysylka_ulica: fullCustomerData.wysylka_ulica || '',
        wysylka_kod_pocztowy: fullCustomerData.wysylka_kod_pocztowy || '',
        wysylka_miasto: fullCustomerData.wysylka_miasto || '',
        wysylka_kraj: fullCustomerData.wysylka_kraj || 'Polska',
        wysylka_kraj_kod: fullCustomerData.wysylka_kraj_kod || 'PL',
        wysylka_telefon: fullCustomerData.wysylka_telefon || '',
        wysylka_telefon_kod: '+48',
        karta_lojalnosciowa_1: fullCustomerData.karta_lojalnosciowa_1 || '',
        karta_lojalnosciowa_2: fullCustomerData.karta_lojalnosciowa_2 || '',
        uwagi: fullCustomerData.uwagi || ''
      };
      setEditingCustomer(customerToEdit);
      setShowEditCustomerModal(true);
    } catch (err) {
      console.error('Blad pobierania danych klienta:', err);
      toast.error('Nie udalo sie pobrac danych klienta');
    }
  };

  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    try {
      const customerData = {
        ...editingCustomer,
        telefon: editingCustomer.telefon ? `${editingCustomer.telefon_kod || '+48'}${editingCustomer.telefon.replace(/^\+\d+/, '')}` : '',
        wysylka_telefon: editingCustomer.wysylka_telefon ? `${editingCustomer.wysylka_telefon_kod || '+48'}${editingCustomer.wysylka_telefon.replace(/^\+\d+/, '')}` : ''
      };
      const updatedCustomer = await customerService.updateCustomer(editingCustomer.id, customerData);
      setSelectedCustomer({
        ...selectedCustomer,
        ...updatedCustomer,
        name: updatedCustomer.nazwa_firmy || `${updatedCustomer.imie || ''} ${updatedCustomer.nazwisko || ''}`.trim(),
        phone: updatedCustomer.telefon
      });
      setShowEditCustomerModal(false);
      setEditingCustomer(null);
    } catch (err) {
      console.error('Blad aktualizacji klienta:', err);
      toast.error('Blad aktualizacji klienta: ' + (err.message || 'Nieznany blad'));
    }
  };

  const removeCustomer = () => {
    setSelectedCustomer(null);
    setCustomerSearchQuery("");
  };

  return {
    // State
    selectedCustomer,
    setSelectedCustomer,
    customerSearchQuery,
    setCustomerSearchQuery,
    customerSuggestions,
    showCustomerSuggestions,
    setShowCustomerSuggestions,
    customerSearchLoading,
    showAddCustomerModal,
    setShowAddCustomerModal,
    showEditCustomerModal,
    setShowEditCustomerModal,
    editingCustomer,
    setEditingCustomer,
    showCustomerHistoryModal,
    setShowCustomerHistoryModal,
    // Actions
    loadAllCustomers,
    selectCustomer,
    handleNewCustomerAdded,
    handleEditSelectedCustomer,
    handleUpdateCustomer,
    removeCustomer
  };
};

export default useCustomerManagement;
